export type ProjectCategory = 'All' | 'AI' | 'SaaS' | 'MVP' | 'Enterprise' | 'Landing Page'

export interface Project {
  slug: string
  title: string
  role: string
  description: string
  shortDescription: string
  category: ProjectCategory[]
  images: string[]
  video?: string
  skills: string[]
  integrations?: string[]
  challenges?: string[]
  solutions?: string[]
  thumbnail: string
}

export const projects: Project[] = [
  {
    slug: 'ai-powered-voice-sales-assistant',
    title: 'AI-Powered Voice Sales Assistant',
    role: 'Senior Front-End Engineer',
    description:
      'Built an interactive AI-powered sales assistant with a conversational avatar, enabling users to speak directly with the agent. Implemented real-time transcript display, dynamic action modals, and a smooth user experience using React, TypeScript, and modern UI libraries.',
    shortDescription:
      'Interactive AI-powered sales assistant with conversational avatar and real-time transcript display.',
    category: ['AI', 'SaaS'],
    images: [
      '/assets/project1/File1.png',
      '/assets/project1/File2.png',
      '/assets/project1/File3.png',
      '/assets/project1/File4.png',
      '/assets/project1/File5.png',
      '/assets/project1/File6.png',
    ],
    skills: ['React', 'TypeScript', 'Front-End Development', 'JavaScript', 'ElevenLabs'],
    integrations: ['ElevenLabs', 'Voice AI', 'Real-time Transcription'],
    challenges: [
      'Implementing real-time voice transcription with low latency.',
      'Creating smooth conversational UI flows.',
      'Integrating dynamic action modals for user interactions.',
    ],
    solutions: [
      'Built WebSocket-based transcript updates for low-latency streaming.',
      'Designed modular conversational components with state management.',
      'Created reusable modal patterns for bookings and actions.',
    ],
    thumbnail: '/assets/project1/File1.png',
  },
  {
    slug: 'saas-platform-ai-agents',
    title: 'SaaS Platform to Create and Manage AI Agents',
    role: 'Senior Front-End Engineer | UI Architect',
    description:
      'A React-based business portal for creating and managing AI sales agents that handle customer interactions and lead generation. Built with React 18 + TypeScript + Refine Framework + Ant Design, featuring Redux Toolkit + React Query, Firebase, Stripe, and 11Labs for voice AI.',
    shortDescription:
      'Business portal for creating and managing AI sales agents with integrated payment and voice capabilities.',
    category: ['AI', 'SaaS'],
    images: [
      '/assets/project2/File1.png',
      '/assets/project2/File2.png',
      '/assets/project2/File3.png',
      '/assets/project2/File4.png',
      '/assets/project2/File5.png',
      '/assets/project2/File6.png',
      '/assets/project2/File7.png',
    ],
    skills: ['React', 'Ant Design', 'TypeScript', 'Redux Toolkit', 'React Query'],
    integrations: ['Firebase', 'Stripe', '11Labs', 'Google OAuth'],
    challenges: [
      'Building scalable admin dashboard for AI agent management.',
      'Integrating Firebase, Stripe, and 11Labs APIs seamlessly.',
      'Managing complex async state flows across multiple modules.',
    ],
    solutions: [
      'Used Refine Framework to modularize business logic.',
      'Created reusable, theme-consistent UI components.',
      'Optimized Redux Toolkit + React Query for performance.',
    ],
    thumbnail: '/assets/project2/File1.png',
  },
  {
    slug: 'presto-workspace-collaboration',
    title: 'Presto - AI-Powered Workspace Collaboration Platform',
    role: 'Principal Front-End Engineer',
    description:
      'A comprehensive AI-powered workspace platform that enables teams to collaborate better and faster. Includes intelligent workspace management, document co-editing, AI assistants, and analytics dashboards.',
    shortDescription:
      'AI-powered workspace collaboration platform with intelligent management and document editing.',
    category: ['AI', 'Enterprise', 'SaaS'],
    images: [
      '/assets/project3/File1.png',
      '/assets/project3/File2.png',
      '/assets/project3/File3.png',
      '/assets/project3/File4.png',
      '/assets/project3/File5.png',
      '/assets/project3/File6.png',
    ],
    video: '/assets/project3/Video1.mov',
    skills: ['React', 'Redux', 'Stripe', 'Auth0', 'Tailwind CSS', 'BlockNote'],
    integrations: ['Auth0', 'Stripe', 'BlockNote Editor', 'AI Integration'],
    challenges: [
      'Managing real-time collaboration with AI-driven features.',
      'Integrating LLM assistance into text editing.',
      'Optimizing for scalability across enterprise clients.',
    ],
    solutions: [
      'Implemented Redux Saga for async workflows.',
      'Integrated BlockNote editor with contextual AI capabilities.',
      'Created modular workspace architecture for scalability.',
    ],
    thumbnail: '/assets/project3/File1.png',
  },
  {
    slug: 'presto-design-system',
    title: 'Presto Design System',
    role: 'Principal Front-End Engineer',
    description:
      'A full React-based component library built with TypeScript and Tailwind CSS. Over 60+ reusable components for building consistent, performant SaaS interfaces.',
    shortDescription:
      'React + Tailwind CSS design system with 60+ reusable components.',
    category: ['Enterprise', 'SaaS'],
    images: ['/assets/project4/File1.png'],
    skills: ['Storybook', 'Tailwind CSS', 'React', 'TypeScript', 'Component Library'],
    integrations: ['Storybook', 'Tailwind CSS'],
    challenges: [
      'Ensuring design consistency across 60+ UI components.',
      'Maintaining accessibility and documentation quality.',
      'Reducing UI build time for new product modules.',
    ],
    solutions: [
      'Created unified design tokens using Tailwind.',
      'Integrated Storybook for developer documentation.',
      'Standardized component patterns across Presto ecosystem.',
    ],
    thumbnail: '/assets/project4/File1.png',
  },
  {
    slug: 'lex-litigator',
    title: 'Lex Litigator (Animated Landing Page)',
    role: 'Tech Lead / Front-End Engineer',
    description:
      'Lex Litigator is a landing page for a legal-tech product designed to streamline law firm workflows. The page uses animated motion sections, scroll-based transitions, and integrated video storytelling to highlight the product\'s features and capabilities.',
    shortDescription:
      'Animated landing page for a legal-tech platform streamlining litigation workflows and management.',
    category: ['Enterprise', 'MVP'],
    images: [
      '/assets/project5/File1.png',
      '/assets/project5/File2.png',
      '/assets/project5/File3.png',
    ],
    video: '/assets/project5/Video1.mov',
    skills: ['React', 'Next.js', 'TypeScript', 'Framer Motion', 'Tailwind CSS'],
    integrations: ['Animation Framework', 'Video Embedding'],
    challenges: [
      'Implementing smooth scroll-triggered animations.',
      'Maintaining high performance with embedded video content.',
      'Designing interactive UI elements aligned with legal domain aesthetics.',
    ],
    solutions: [
      'Used Framer Motion for fluid transitions and scroll animations.',
      'Optimized video rendering with lazy loading and compressed assets.',
      'Developed modular animation hooks for reusable UI patterns.',
    ],
    thumbnail: '/assets/project5/File1.png',
  },
  {
    slug: 'zatcax-landing-page',
    title: 'ZatcaX (Landing Page)',
    role: 'Tech Lead / Front-End Engineer',
    description:
      'Landing page for ZatcaX, an invoicing SaaS platform built for ZATCA compliance. Designed to highlight features like digital invoicing, secure payment integration, and analytics dashboards with a strong conversion focus.',
    shortDescription:
      'ZATCA-compliant invoicing SaaS landing page with animation and CTA-driven layout.',
    category: ['Landing Page', 'MVP'],
    images: [
      '/assets/project6/File1.png',
      '/assets/project6/File2.png',
      '/assets/project6/File3.png',
      '/assets/project6/File4.png',
    ],
    video: '/assets/project6/Video1.mov',
    skills: ['Next.js', 'React', 'Tailwind CSS', 'Framer Motion'],
    integrations: ['Stripe', 'Analytics', 'Contact Form'],
    challenges: [
      'Creating an animated landing experience optimized for conversion.',
      'Integrating marketing analytics and lead tracking.',
      'Ensuring compliance and localization readiness.',
    ],
    solutions: [
      'Used Framer Motion for dynamic feature reveal animations.',
      'Integrated analytics hooks for user behavior insights.',
      'Implemented responsive, fast-loading page with Next.js optimizations.',
    ],
    thumbnail: '/assets/project6/File1.png',
  },
  {
    slug: 'zatcax-invoicing-saas',
    title: 'ZatcaX (Invoicing SaaS Application)',
    role: 'Tech Lead / Front-End Engineer',
    description:
      'A full invoicing SaaS MVP to help businesses manage invoices, payments, and financial tracking with ZATCA compliance. Includes secure payment integration, financial dashboards, and automated invoice generation.',
    shortDescription:
      'ZATCA-compliant invoicing SaaS application with payment integration and analytics dashboards.',
    category: ['MVP', 'SaaS'],
    images: [
      '/assets/project7/File1.png',
      '/assets/project7/File2.png',
      '/assets/project7/File3.png',
      '/assets/project7/File4.png',
    ],
    video: '/assets/project7/Video1.mov',
    skills: ['Next.js', 'TypeScript', 'MVP Development', 'Payment Integration'],
    integrations: ['Stripe', 'Payment Processing', 'ZATCA Compliance'],
    challenges: [
      'Building compliant invoicing workflows aligned with ZATCA guidelines.',
      'Integrating Stripe securely for multi-user billing.',
      'Designing intuitive dashboards for financial insights.',
    ],
    solutions: [
      'Implemented reusable components from the landing system for SaaS app.',
      'Integrated secure payment handling with Stripe API.',
      'Developed financial dashboards with real-time updates and charts.',
    ],
    thumbnail: '/assets/project7/File1.png',
  },
]

export function getProjectBySlug(slug: string): Project | undefined {
  return projects.find((project) => project.slug === slug)
}

export function getProjectsByCategory(category: ProjectCategory): Project[] {
  if (category === 'All') {
    return projects
  }
  return projects.filter((project) => project.category.includes(category))
}

